import { useMemo, useState } from 'react';
import { Car, Check, Door, DoorOpen, Hash, Ruler, Storefront, User, Warehouse, type Icon } from '@phosphor-icons/react';
import { Auswahl, FeldRaster, ImmoButton, LivePreviewCard, StepForm, TextFeld } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { eur, qm } from '../../../../../lib/immobilien/format';
import { parseDezimal } from '../../../../../lib/immobilien/stammdaten';
import {
  LAGEN, NEUE_ARTEN, ZIMMER, bezeichnungsVorschlag, brauchtFlaeche, einheitEinfuegen, gesamtAusEntwurf, hatZimmer, leererEntwurf,
  mieterEinfuegen, pruefeEntwurf, type NeueArt, type NeueEinheitEntwurf,
} from '../../../../../lib/immobilien/einheitenTab';
import { legeMieterOrdnerAn } from '../../../../../lib/wechsel/api';
import { useObjektSeite } from '../ObjektSeitenKontext';

const ART_ICON: Record<NeueArt, Icon> = { wohnung: Door, garage: Warehouse, stellplatz: Car, gewerbe: Storefront };

/**
 * „Neue Einheit“ (README §5.4, Screens 21/22): drei Schritte Art · Lage & Größe · Vermietung,
 * Live-Vorschau, Fortschrittsbalken. Bei „Vermietet“ wird der Mieter wie im bestehenden
 * Mieter-Formular angelegt (inkl. Drive-Ordner, best-effort).
 */
export function NeueEinheitFormular({ hausId, onAbbrechen, onAngelegt }: {
  hausId: string | null;
  onAbbrechen: () => void;
  onAngelegt: (einheitId: string, bezeichnung: string) => void;
}) {
  const { daten, uebersicht } = useObjektSeite();
  const [e, setE] = useState<NeueEinheitEntwurf>(() => leererEntwurf(hausId));
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const setze = <K extends keyof NeueEinheitEntwurf>(k: K) => (v: NeueEinheitEntwurf[K]) => setE(x => ({ ...x, [k]: v }));

  const vorschlag = bezeichnungsVorschlag(uebersicht.length, e.art, e.lage);
  const pruefung = useMemo(() => pruefeEntwurf(e, uebersicht.map(u => u.bezeichnung), vorschlag), [e, uebersicht, vorschlag]);
  const bereit = pruefung.offen.length === 0;
  const gesamt = gesamtAusEntwurf(e);
  const flaeche = parseDezimal(e.flaeche);
  const IconArt = ART_ICON[e.art];

  const speichere = async () => {
    if (!bereit) return;
    setSpeichert(true);
    setFehler(null);
    try {
      const zeile = einheitEinfuegen(e, daten.objekt.id, vorschlag);
      const { data, error } = await supabase.from('objekt_einheiten').insert(zeile).select('id').single();
      if (error) throw new Error(error.message);
      const einheitId = (data as { id: string }).id;
      const bezeichnung = String(zeile.bezeichnung);
      if (e.vermietet) {
        const m = await supabase.from('mieter').insert(mieterEinfuegen(e, daten.objekt.id, einheitId, bezeichnung)).select('id').single();
        if (m.error) throw new Error(`Einheit angelegt, Mieter nicht: ${m.error.message}`);
        // Drive-Ordner wie im Mieter-Formular (best-effort)
        legeMieterOrdnerAn((m.data as { id: string }).id).catch(err => console.error('Mieter-Ordner anlegen fehlgeschlagen:', err));
      }
      onAngelegt(einheitId, bezeichnung);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      <span style={{ fontSize: 18, fontWeight: 800 }}>Neue Einheit anlegen</span>
      {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
      <StepForm
        hinweis={bereit ? 'Alles bereit – Einheit kann angelegt werden' : `Noch offen: ${pruefung.offen.join(', ')}`}
        bereit={bereit}
        speichert={speichert}
        onSpeichern={() => { void speichere(); }}
        speichernLabel="Einheit anlegen"
        sekundaer={<ImmoButton art="fuellung" groesse={48} pille={false} onClick={onAbbrechen}>Abbrechen</ImmoButton>}
        schritte={[
          {
            titel: 'Art', untertitel: 'Wohnung, Garage, Stellplatz oder Gewerbe', erledigt: pruefung.schritte[0],
            inhalt: <Auswahl art="kachel" wert={e.art} onChange={setze('art')} optionen={NEUE_ARTEN.map(a => ({ key: a.key, label: a.label, icon: ART_ICON[a.key] }))} />,
          },
          {
            titel: 'Lage & Größe', untertitel: 'Geschoss, Bezeichnung und Fläche', erledigt: pruefung.schritte[1],
            inhalt: (
              <div className="flex flex-col" style={{ gap: 12 }}>
                <Auswahl label="Lage" wert={e.lage} onChange={setze('lage')} optionen={LAGEN.map(l => ({ key: l, label: l }))} />
                <FeldRaster minBreite={170}>
                  <TextFeld label="Bezeichnung" icon={Hash} wert={e.bezeichnung} onChange={setze('bezeichnung')} placeholder={vorschlag} />
                  <TextFeld label={brauchtFlaeche(e.art) ? 'Fläche (m²)' : 'Fläche (m², optional)'} icon={Ruler} wert={e.flaeche} onChange={setze('flaeche')} inputMode="decimal" placeholder="z. B. 72,5" />
                </FeldRaster>
                {hatZimmer(e.art) && <Auswahl label="Zimmer" wert={e.zimmer} onChange={setze('zimmer')} optionen={ZIMMER.map(z => ({ key: z, label: z }))} />}
              </div>
            ),
          },
          {
            titel: 'Vermietung', untertitel: 'Frei oder direkt mit Mieter anlegen', erledigt: pruefung.schritte[2],
            inhalt: (
              <div className="flex flex-col" style={{ gap: 12 }}>
                <Auswahl label="Status" wert={e.vermietet ? 'v' : 'f'} onChange={v => setze('vermietet')(v === 'v')}
                  optionen={[{ key: 'f', label: 'Frei / Leerstand', icon: DoorOpen }, { key: 'v', label: 'Vermietet', icon: User }]} />
                {e.vermietet && (
                  <FeldRaster minBreite={150}>
                    <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
                      <label htmlFor="neu-anrede" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Anrede</label>
                      <select id="neu-anrede" value={e.anrede} onChange={x => setze('anrede')(x.target.value)}
                        style={{ height: 50, borderRadius: 15, padding: '0 14px', border: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                        {['Herr', 'Frau', 'Divers', 'Sonstige'].map(a => <option key={a}>{a}</option>)}
                      </select>
                    </div>
                    <TextFeld label="Vorname" wert={e.vorname} onChange={setze('vorname')} />
                    <TextFeld label="Nachname" wert={e.nachname} onChange={setze('nachname')} />
                    <TextFeld label="Mietbeginn" type="date" wert={e.mietbeginn} onChange={setze('mietbeginn')} />
                    <TextFeld label="Kaltmiete" wert={e.kalt} onChange={setze('kalt')} inputMode="decimal" suffix="€" />
                    <TextFeld label="Nebenkosten" wert={e.nk} onChange={setze('nk')} inputMode="decimal" suffix="€" />
                    <TextFeld label="Heizkosten" wert={e.hk} onChange={setze('hk')} inputMode="decimal" suffix="€" />
                    <div className="flex flex-col justify-end" style={{ gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Gesamtmiete</span>
                      <span className="immo-zahl" style={{ height: 50, display: 'flex', alignItems: 'center', fontSize: 18, fontWeight: 800 }}>{eur(gesamt)}</span>
                    </div>
                  </FeldRaster>
                )}
              </div>
            ),
          },
        ]}
        vorschau={
          <LivePreviewCard>
            <div className="flex items-center min-w-0" style={{ gap: 12 }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: 15, background: e.vermietet ? 'var(--acc-soft)' : 'var(--warn-soft)' }}>
                <IconArt weight="fill" size={22} color={e.vermietet ? 'var(--acc)' : 'var(--warn)'} />
              </span>
              <div className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
                <span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{e.bezeichnung.trim() || vorschlag}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                  {NEUE_ARTEN.find(a => a.key === e.art)?.label} · {e.lage}{flaeche ? ` · ${qm(flaeche)}` : ''}{hatZimmer(e.art) ? ` · ${e.zimmer} Zi.` : ''}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center" style={{ gap: 8 }}>
              <span style={{ height: 24, padding: '0 10px', borderRadius: 12, display: 'flex', alignItems: 'center', fontSize: 11, fontWeight: 800,
                background: e.vermietet ? 'var(--ok-soft)' : 'var(--warn-soft)', color: e.vermietet ? 'var(--ok)' : 'var(--warn)' }}>
                {e.vermietet ? 'Vermietet' : 'Leerstand'}
              </span>
              <span className="immo-zahl" style={{ fontSize: 22, fontWeight: 800 }}>{e.vermietet ? eur(gesamt) : '–'}</span>
            </div>
            <div className="flex flex-col" style={{ gap: 6, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink2)' }}>
              <span className="flex items-center" style={{ gap: 8 }}><User size={14} />{e.vermietet ? `${e.vorname} ${e.nachname}`.trim() || 'Mieter fehlt' : 'Noch kein Mieter'}</span>
              <span className="flex items-center" style={{ gap: 8 }}><Ruler size={14} />
                {e.vermietet && flaeche && (parseDezimal(e.kalt) ?? 0) > 0 ? `Kalt ${eur(Math.round(((parseDezimal(e.kalt) ?? 0) / flaeche) * 100) / 100)}/m²` : 'Kalt/m² –'}
              </span>
              {pruefung.offen.length === 0 && <span className="flex items-center" style={{ gap: 8, color: 'var(--ok)', fontWeight: 700 }}><Check weight="bold" size={14} />Bereit</span>}
            </div>
          </LivePreviewCard>
        }
      />
    </div>
  );
}
